import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection } from './LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="September 1, 2026" testId="privacy-page">
      <LegalSection title="Who we are">
        <p>
          Sourcetape is a quality harness for AI agent skills, operated by Alex Casanova
          (&quot;Sourcetape,&quot; &quot;we,&quot; &quot;us&quot;). The service lives at{' '}
          <a href="https://sourcetape.io" className="text-accent hover:underline">sourcetape.io</a>.
          This policy describes how we handle information when you visit the site, open Studio,
          create an account, or buy credits.
        </p>
      </LegalSection>

      <LegalSection title="What we collect">
        <p>We collect only what the product needs to run:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-textp">Account.</span> Name, email, and a password hash if you
            register with email. We never store or return the plaintext password. If you sign in
            with Google, we receive your verified email, name, and Google account id. Google sign-in
            uses the <code className="text-textp">openid email profile</code> scopes. If you set a
            light or dark appearance, we store that preference on your account.
          </li>
          <li>
            <span className="text-textp">Projects and skills.</span> Outcome descriptions, canvas
            pipelines, skill text, fixtures, chat threads you send to the hosted API, and exported
            <code className="text-textp"> SKILL.md</code> content you ask us to store.
          </li>
          <li>
            <span className="text-textp">LLM settings.</span> Provider choice, model, base URL, and
            any OpenAI or Anthropic API keys you save on your account. Keys are stored
            server-side and are never returned in API responses. We do not persist API keys in the
            browser.
          </li>
          <li>
            <span className="text-textp">Billing.</span> Credit balance, pack purchases, and
            subscription status. Card numbers go to Stripe, not to us. We keep Stripe checkout
            session identifiers so a completed payment is credited once.
          </li>
          <li>
            <span className="text-textp">On-device data.</span> Studio keeps the session token,
            a copy of your user record, per-project chat/tab state, and your light/dark theme
            choice in browser{' '}
            <code className="text-textp">localStorage</code>. Guest Studio can run without an
            account. That local data stays on your machine until you clear it.
          </li>
          <li>
            <span className="text-textp">Usage analytics.</span> Google Analytics 4 records
            page views and basic device/browser data on sourcetape.io so we can see which
            pages people use. We do not send your email, project content, or API keys to
            Analytics. Local development and the desktop app do not load the tag.
          </li>
        </ul>
        <p>
          We do not run advertising pixels. Server logs (IP, user agent, request path, time)
          exist for operations and abuse prevention and are not used to profile you.
        </p>
      </LegalSection>

      <LegalSection title="How we use it">
        <ul className="list-disc pl-5 space-y-2">
          <li>Create and authenticate your account, including Google SSO.</li>
          <li>Save projects, skills, and settings, and restore them when you return.</li>
          <li>
            Run hosted scores, judges, and improvement loops when you spend credits. That work
            sends skill text, fixtures, and related prompts to the model provider you selected
            (or our hosted judge when you buy a hosted score).
          </li>
          <li>Call your BYOK provider with the key you saved, only for requests you start.</li>
          <li>Process credit packs through Stripe and apply the resulting balance.</li>
          <li>See which pages people visit, via Google Analytics, so we can improve the site.</li>
          <li>Respond to support mail and keep the service reliable and secure.</li>
        </ul>
        <p>
          Local schema tests and LM Studio runs stay on your machine. Guest Studio does not
          require an account. Sign in when you want saved projects, a credit wallet, or hosted
          runs.
        </p>
      </LegalSection>

      <LegalSection title="Processors">
        <p>We share data with the services that actually run the product:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-textp">Google.</span> Sign-in and Google Analytics. See{' '}
            <a href="https://policies.google.com/privacy" className="text-accent hover:underline">Google&apos;s privacy policy</a>.
          </li>
          <li>
            <span className="text-textp">Stripe.</span> Checkout and payment confirmation. See{' '}
            <a href="https://stripe.com/privacy" className="text-accent hover:underline">Stripe&apos;s privacy policy</a>.
          </li>
          <li>
            <span className="text-textp">Amazon Web Services.</span> Hosting (CloudFront, S3, ECS)
            for sourcetape.io.
          </li>
          <li>
            <span className="text-textp">MongoDB Atlas.</span> Hosted account, project, skill, and
            settings storage.
          </li>
          <li>
            <span className="text-textp">OpenAI, Anthropic, and OpenRouter.</span> Only when you
            (or a hosted score you paid for) send a completion to that provider. Their policies
            apply to the prompts and outputs they receive.
          </li>
        </ul>
        <p>
          We do not sell your personal information. We do not share it for cross-context
          behavioral advertising.
        </p>
      </LegalSection>

      <LegalSection title="Cookies and local storage">
        <p>
          Auth uses a JWT in <code className="text-textp">localStorage</code>, not an httpOnly
          cookie. Google Analytics may set first-party cookies such as{' '}
          <code className="text-textp">_ga</code> on sourcetape.io. We do not set advertising
          cookies. Clearing site data signs you out, drops locally cached Studio chats, and
          resets Analytics cookies. The desktop app stores the same kind of data in the OS
          application-data directory when you run it locally.
        </p>
      </LegalSection>

      <LegalSection title="Retention">
        <p>
          Account, project, and billing records stay until you ask us to delete them or we close
          the account. Saved API keys stay until you replace them or we delete the account.
          Transaction records needed for accounting may be kept longer where the law requires it.
          Server logs rotate in the ordinary course of operations.
        </p>
      </LegalSection>

      <LegalSection title="Your choices">
        <p>
          You can edit project and skill content in Studio, change or clear saved API keys, and
          sign out (which drops the browser token). You can stop using hosted providers and run
          LM Studio in the desktop app instead. There is no in-app account deletion yet; email{' '}
          <a href="mailto:privacy@sourcetape.io" className="text-accent hover:underline">privacy@sourcetape.io</a>
          {' '}to access, correct, or delete your account data. We will ask enough to confirm
          it is you.
        </p>
        <p>
          If you are in the EEA, UK, or a similar jurisdiction, you may also have rights to
          access, rectify, erase, restrict, or port your data, and to object to processing.
          You can complain to your local data protection authority.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          Sourcetape is a developer tool. It is not directed at children under 16, and we do
          not knowingly collect personal information from them.
        </p>
      </LegalSection>

      <LegalSection title="Changes">
        <p>
          If this policy changes, we will update the date at the top of this page. Continued
          use after a change means you accept the updated policy. Material changes will be
          called out here.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Privacy questions: <a href="mailto:privacy@sourcetape.io" className="text-accent hover:underline">privacy@sourcetape.io</a>.
          The <Link to="/terms-of-use" className="text-accent hover:underline">Terms of Use</Link> cover
          accounts, credits, and acceptable use.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
